import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      // Schedule auto-refresh 5 minutes before expiry (refresh every 23h for daily, 6d23h for weekly)
      scheduleRefresh();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Auto-refresh every 20 hours to stay ahead of 24h expiry
    refreshTimerRef.current = setTimeout(async () => {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        scheduleRefresh(); // Reschedule after refresh
      } catch {
        // Refresh failed — token expired, need re-login
        setUser(false);
      }
    }, 20 * 60 * 60 * 1000); // 20 hours
  }, []);

  useEffect(() => { checkAuth(); return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); }; }, [checkAuth]);

  const logout = async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
